import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Clock } from 'lucide-react';

export default function PendingApprovalPage() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();

  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (user?.isApproved) {
      navigate('/', { replace: true });
    }
  }, [navigate, user?.isApproved]);

  const handleCheckStatus = async () => {
    setChecking(true);
    try {
      const updatedUser = await refreshUser();
      if (updatedUser?.isApproved) {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to check approval status:', error);
      toast.error('Failed to check approval status');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 text-center">
        <div>
          <div className="mx-auto h-24 w-24 rounded-full bg-warning/15 flex items-center justify-center">
            <Clock className="h-12 w-12 text-warning" />
          </div>
          <h2 className="mt-6 text-2xl font-semibold font-heading tracking-tight">
            Your account is being reviewed
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            We'll get you set up shortly.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-center gap-3">
              {user?.avatar && (
                <img
                  src={user.avatar}
                  alt={user.name || 'User'}
                  className="h-10 w-10 rounded-full"
                />
              )}
              <div className="text-left">
                <p className="text-sm font-medium">{user?.name || 'User'}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            <Separator />

            <p className="text-sm text-muted-foreground">
              An administrator needs to approve your account and assign API access before you can use GIFT.
            </p>

            <Separator />

            <div className="space-y-3">
              <Button className="w-full" onClick={handleCheckStatus} disabled={checking}>
                {checking ? (
                  <>
                    <Spinner className="w-4 h-4 mr-2" />
                    Checking...
                  </>
                ) : (
                  'Check Status'
                )}
              </Button>
              <Button variant="outline" className="w-full" onClick={logout}>
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
